import { Container, clx } from "@modules/common/components/ui"
import Image from "next/image"
import React from "react"

import PlaceholderImage from "@modules/common/icons/placeholder-image"

type ThumbnailProps = {
  thumbnail?: string | null
  images?: { url?: string }[] | null
  size?: "small" | "medium" | "large" | "full" | "square"
  isFeatured?: boolean
  className?: string
  // Accessible description of the pictured product, e.g. its title.
  alt?: string
  "data-testid"?: string
}

const IMAGE_SIZES =
  "(max-width: 576px) 280px, (max-width: 768px) 360px, (max-width: 992px) 480px, 800px"

const Thumbnail: React.FC<ThumbnailProps> = ({
  thumbnail,
  images,
  size = "small",
  className,
  alt,
  "data-testid": dataTestid,
}) => {
  const initialImage = thumbnail || images?.[0]?.url
  // Second product shot (straight vs. angled) used for the hover crossfade.
  const hoverImage = images?.find(
    (image) => image.url && image.url !== initialImage
  )?.url

  return (
    <Container
      className={clx(
        "relative w-full overflow-hidden bg-white aspect-square shadow-elevation-card-rest rounded-large group-hover:shadow-elevation-card-hover transition-shadow ease-out duration-200",
        className,
        {
          "w-[180px]": size === "small",
          "w-[290px]": size === "medium",
          "w-[440px]": size === "large",
          "w-full": size === "full",
        }
      )}
      data-testid={dataTestid}
    >
      <ImageOrPlaceholder
        image={initialImage}
        hoverImage={hoverImage}
        size={size}
        alt={alt}
      />
    </Container>
  )
}

const ImageOrPlaceholder = ({
  image,
  hoverImage,
  size,
  alt,
}: Pick<ThumbnailProps, "size"> & {
  image?: string
  hoverImage?: string
  alt?: string
}) => {
  return image ? (
    <>
      <Image
        src={image}
        alt={alt ?? "Product thumbnail"}
        className={clx(
          "absolute inset-0 object-contain object-center p-4 transition-all duration-300 ease-in-out group-hover:scale-[1.03]",
          hoverImage && "group-hover:opacity-0"
        )}
        draggable={false}
        quality={50}
        sizes={IMAGE_SIZES}
        fill
      />
      {hoverImage && (
        <Image
          src={hoverImage}
          alt={alt ? `${alt} — alternate view` : "Product alternate view"}
          className="absolute inset-0 object-contain object-center p-4 opacity-0 transition-all duration-300 ease-in-out group-hover:opacity-100 group-hover:scale-[1.03]"
          draggable={false}
          quality={50}
          sizes={IMAGE_SIZES}
          fill
        />
      )}
    </>
  ) : (
    <div className="w-full h-full absolute inset-0 flex items-center justify-center">
      <PlaceholderImage size={size === "small" ? 16 : 24} />
    </div>
  )
}

export default Thumbnail
